import { NavLink } from 'react-router';
import { useTranslation } from 'react-i18next';
import { ChevronDown } from 'lucide-react';
import { hasAnyPermission, isOrgAdmin, type SessionSnapshot } from '@/auth/types';
import { useNavGroupStore } from './nav-groups';
import type { MenuItem } from './menus';
import styles from './AppShell.module.css';

export type SidebarNavProps = {
  items: readonly MenuItem[];
  snapshot: SessionSnapshot | null;
  /** Ogeye tiklandiginda cagrilir — dar ekranda cekmeceyi kapatmak icin. */
  onNavigate?: () => void;
};

/**
 * Menuyu VERIDEN cizer (bkz. menus.ts). Gorunurluk kurallari tek yerde:
 *
 *   adminOnly  -> yalniz buro yoneticisi
 *   permission -> grant listesinde var mi (YONETICI SINANMAZ, ortuk yetkili)
 *
 * Bu bir GUVENLIK SINIRI DEGIL: gizlenen bir baglantinin adresi elle yazilirsa
 * rota yine acilir ve RequirePermission ret panelini gosterir; asil denetim
 * sunucuda. Buradaki filtre, kullaniciya calismayacak bir baglanti
 * gostermemek icin var.
 */
const isVisible = (item: MenuItem, snapshot: SessionSnapshot | null): boolean => {
  // Kenar cubugunda cizilmeyenler: kabuk onlari sag ust kosede kendisi cizer
  // (profil). Menuden SILINMIYORLAR cunku rotalar o listeden uretiliyor.
  if (item.placement === 'topbar') return false;
  if (item.adminOnly && !isOrgAdmin(snapshot)) return false;
  if (item.requiresOrganization && !snapshot?.organization) return false;
  if (item.permission) {
    const codes = typeof item.permission === 'string' ? [item.permission] : item.permission;
    if (!hasAnyPermission(snapshot, codes)) return false;
  }
  return true;
};

type MenuGroup = { key: string | null; items: MenuItem[] };

/**
 * Bitisik ayni `groupKey`'e sahip ogeleri tek gruba toplar.
 *
 * SIRA ONEMLI: gruplama FILTRELEMEDEN SONRA yapilir. Once gruplayip sonra
 * filtreleseydik, tum ogeleri gizlenmis bir grup (ornegin yetkisi olmayan
 * personel icin "Analiz") ekranda BOS BIR BASLIK olarak kalirdi.
 */
const groupItems = (items: readonly MenuItem[]): MenuGroup[] => {
  const groups: MenuGroup[] = [];
  for (const item of items) {
    const key = item.groupKey ?? null;
    const last = groups.at(-1);
    if (last && last.key === key) last.items.push(item);
    else groups.push({ key, items: [item] });
  }
  return groups;
};

export function SidebarNav({ items, snapshot, onNavigate }: SidebarNavProps) {
  const { t } = useTranslation();
  const collapsed = useNavGroupStore((state) => state.collapsed);
  const toggle = useNavGroupStore((state) => state.toggle);
  const groups = groupItems(items.filter((item) => isVisible(item, snapshot)));

  return (
    <nav className={styles.nav} aria-label={t('nav.primary')}>
      {groups.map((group) => {
        // Yerel degiskene aliniyor ki `onClick` icinde daraltma (narrowing)
        // korunsun; `group.key` uzerinden gidilirse TS onu callback icinde
        // yeniden `string | null` sayar ve cast gerekirdi.
        const groupKey = group.key;
        const isOpen = groupKey === null || !collapsed.includes(groupKey);
        const bodyId = groupKey ? `navgroup-body-${groupKey}` : undefined;

        return (
          <div key={groupKey ?? '__ungrouped'} className={styles.navGroup}>
            {/* Baslik bir DUGME (basliga sarili degil): tiklanabilir oldugu icin
                klavye ve ekran okuyucu ona bir dugme gibi davranmali.
                `aria-expanded` + `aria-controls` durumu soyler; gorsel ok salt
                dekoratif. */}
            {groupKey ? (
              <button
                type="button"
                className={styles.navGroupTitle}
                aria-expanded={isOpen}
                aria-controls={bodyId}
                onClick={() => {
                  toggle(groupKey);
                }}
              >
                <span>{t(groupKey)}</span>
                <ChevronDown
                  size={16}
                  aria-hidden="true"
                  className={[styles.navGroupChevron, isOpen ? undefined : styles.navGroupChevronClosed]
                    .filter(Boolean)
                    .join(' ')}
                />
              </button>
            ) : null}

            {/* Kapaliyken de DOM'da duruyor (yukseklik animasyonu `display:none`
                ile calismaz), bu yuzden gizli baglantilarin SEKME SIRASINDAN
                cikmasi ayrica saglanmali — gorunmeyen bir baglantiya Tab ile
                gitmek en sinir bozucu erisilebilirlik hatalarindan biri.
                Iki katman birden:
                  `inert`  — odak ve erisilebilirlik agacindan cikarir, DUZENI
                             bozmadigi icin animasyon calismaya devam eder.
                  CSS      — `visibility: hidden` gorsel gizleme.
                `inert` tek basina yeterli olurdu ama CSS'e BAGLI OLMAYAN bir
                garanti istiyoruz: sinif adi degisse ya da stil yuklenmese bile
                gizli baglantilar odaklanabilir olmamali. */}
            <div
              {...(bodyId ? { id: bodyId } : {})}
              {...(isOpen ? {} : { inert: true })}
              className={[styles.navGroupBody, isOpen ? undefined : styles.navGroupBodyClosed]
                .filter(Boolean)
                .join(' ')}
            >
              <ul className={styles.navList}>
                {group.items.map((item) => (
                  <li key={item.id}>
                    <NavLink
                      to={item.to}
                      // `end`: bolge kokune giden baglanti, alt rotalardayken de aktif
                      // gorunmesin. Bunsuz "Taleplerim" hep secili kalir.
                      end={!item.to.includes('/', 1)}
                      onClick={onNavigate}
                      className={({ isActive }) =>
                        [styles.navLink, isActive ? styles.navLinkActive : undefined]
                          .filter(Boolean)
                          .join(' ')
                      }
                    >
                      {t(item.labelKey)}
                    </NavLink>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        );
      })}
    </nav>
  );
}
